import { GiSettingsKnobs } from "react-icons/gi";
import { useModals } from "../../contexts/ModalsContext";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { useState, useEffect, useRef } from "react";
import { HiOutlineDocumentDuplicate } from "react-icons/hi2";
import { MdDeleteOutline } from "react-icons/md";
import { ProductCardType } from "../../types/ProductType";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BiSolidHide } from "react-icons/bi";
import { BiSolidShow } from "react-icons/bi";

function ProductButtonsOperations({
  _id,
  FormId,
  setNeedRefetch,
  nameAR,
  nameEN,
  isSending,
  hidden,
}: Pick<ProductCardType, "_id" | "nameAR" | "nameEN" | "hidden"> & {
  FormId: string;
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  isSending: boolean;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { setOpendProductDataModalId } = useModals();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  const DeleteUrl = `${BASE_URL}/api/dashboard/products/${_id}?locale=${lang}`;
  const duplicateUrl = `${BASE_URL}/api/dashboard/products/${_id}/duplicate`;
  const hideUrl = `${BASE_URL}/api/dashboard/products/${_id}?locale=${lang}`;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropDownButtonRef = useRef<HTMLButtonElement>(null);

  const handleDuplicate = async () => {
    const loadingToast = toast.loading(
      t("productButtonOperations.duplicating") as string,
    );
    try {
      const res = await axios.post(
        duplicateUrl,
        {},
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.success === false) {
        toast.error(res.data.error);
        return;
      }

      toast.success(t("productButtonOperations.duplicateSuccess") as string);
      toast.dismiss(loadingToast);
      setNeedRefetch(true);
      setIsDropdownOpen(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          (t("productButtonOperations.duplicateFailed") as string);
        toast.error(message);
      } else {
        toast.error(t("productButtonOperations.error") as string);
      }
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleHideFromMenu = async () => {
    const loadingToast = toast.loading(
      t("productButtonOperations.loading") as string,
    );
    try {
      const res = await axios.put(
        hideUrl,
        {
          hidden: !hidden,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (res.data.success === false) {
        toast.error(res.data.error);
        return;
      }

      toast.success(t("productButtonOperations.hideSuccess") as string);

      toast.dismiss(loadingToast);
      setNeedRefetch(true);
      setIsDropdownOpen(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          (t("productButtonOperations.hideFailed") as string);
        toast.error(message);
      } else {
        toast.error(t("productButtonOperations.error") as string);
      }
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async () => {
    const shouldDelete = window.confirm(
      t("productButtonOperations.confirmDelete", { nameEN, nameAR }) as string,
    );
    if (shouldDelete) {
      const loadingToast = toast.loading(
        t("productButtonOperations.deleting") as string,
      );
      try {
        const res = await axios.delete(DeleteUrl, {
          headers: {
            Authorization: token,
          },
        });

        if (!res.data.success) {
          toast.error(res.data.error);
          return;
        }

        toast.success(t("productButtonOperations.deleteSuccess") as string);
        toast.dismiss(loadingToast);
        setNeedRefetch(true);
        setIsDropdownOpen(false);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t("productButtonOperations.deleteFailed") as string);
          toast.error(message);
        } else {
          toast.error(t("productButtonOperations.error") as string);
        }
      } finally {
        toast.dismiss(loadingToast);
      }
    }
  };

  const handleOpenProductDataModalById = () => {
    setOpendProductDataModalId(_id);
  };

  // Close dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !dropDownButtonRef.current?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    } else {
      setTimeout(() => {
        setIsDropdownOpen(true);
      }, 0);
    }
  };

  return (
    <div className="grid h-[90px] w-full grid-cols-2 grid-rows-2 self-end border-t border-gray-200 text-sm font-semibold text-white">
      {/* Product Data Modal */}
      <button
        onClick={handleOpenProductDataModalById}
        type="button"
        className="col-start-1 col-end-2 row-start-1 flex grid-rows-[1fr,2fr] items-center justify-center gap-2 px-1 py-2 text-black"
      >
        <GiSettingsKnobs className="mt-[2px] rotate-90" size={17} />
        <span> {t("productButtonOperations.productData")}</span>
      </button>

      <button
        onClick={toggleDropdown}
        ref={dropDownButtonRef}
        type="button"
        className="relative col-start-2 col-end-3 row-start-1 row-end-2 flex items-center justify-center gap-3 border-s border-gray-200 px-1 py-2 text-black"
      >
        <span> {t("productButtonOperations.more")}</span>
        <MdOutlineKeyboardArrowDown className="mt-1" size={18} />

        {isDropdownOpen && (
          <div
            ref={dropdownRef}
            className="absolute bottom-[-120px] end-0 z-[11] flex h-[120px] min-w-full w-fit flex-col rounded-md border border-gray-300 bg-white"
          >
            <span
              onClick={handleDuplicate}
              className="flex h-[40px] items-center gap-2 px-2 py-1 text-sm text-black hover:bg-sky-50"
            >
              <HiOutlineDocumentDuplicate className="mt-[2px]" size={17} />
              <span>{t("productButtonOperations.duplicate")}</span>
            </span>
            <span
              onClick={handleHideFromMenu}
              className="flex h-[40px] items-center gap-2 px-2 py-1 text-sm text-black hover:bg-sky-50"
            >
              {hidden ? (
                <BiSolidShow className="mt-[2px]" size={17} />
              ) : (
                <BiSolidHide className="mt-[2px]" size={17} />
              )}
              <span>
                {hidden
                  ? t("productButtonOperations.show_in_menu")
                  : t("productButtonOperations.hide_from_menu")}
              </span>
            </span>
            <span
              onClick={handleDelete}
              className="flex h-[40px] items-center gap-2 px-2 py-1 text-sm text-red-500 hover:bg-sky-50"
            >
              <MdDeleteOutline className="mt-[2px]" size={17} />
              <span> {t("productButtonOperations.delete")}</span>
            </span>
          </div>
        )}
      </button>

      <button
        type="submit"
        disabled={isSending}
        form={`${FormId}-form`}
        className="col-start-1 col-end-3 row-start-2 row-end-3 flex items-center justify-center rounded-b-lg bg-sidebar px-1 py-2 text-skyblue disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
      >
        {t("productButtonOperations.save")}
      </button>
    </div>
  );
}

export default ProductButtonsOperations;
